<script setup lang="ts">
import type { ParsedContent } from '@nuxt/content'
import { arrayify, expandDuration, spellAreaIcon, spellLevel, titleCase } from '../util'
import type { SpellPage } from '../content/SpellListing.vue'
import SpellTableCell from './SpellTableCell.vue'
import { resolveSpell } from '~/assets/spells/spellData';

const props = defineProps<{
  spell: SpellPage | ParsedContent
}>()

const data = computed(() => resolveSpell(props.spell.title ?? ''))

const effects = computed(() => arrayify(props.spell.effects))

const rangeText = computed(() => {
  if (Number.isInteger(data.value?.range)) return `${data.value?.range} ft`
  return titleCase(data.value?.range ?? '')
})

const componentsText = computed(() => {
  const comp = data.value?.components ?? ''
  return comp.split('').map((c) => c.toUpperCase()).join(', ')
})

const saveAliases: Record<string, string> = {
  atk: 'Attack',
  str: 'STR save',
  dex: 'DEX save',
  con: 'CON save',
  int: 'INT save',
  wis: 'WIS save',
  cha: 'CHA save',
}
const saves = computed(() => data.value?.fx?.map(f => saveAliases[f]).filter(f => f !== undefined) ?? [])

const castText = computed(() => {
  const cast = data.value?.cast ?? ''

  const castTimes: Record<string, string> = {
    'a': '1 Action',
    'ba': '1 Bonus Action',
    'ba*': '1 Bonus Action',
    'r': '1 Reaction',
  }

  return castTimes[cast] ?? expandDuration(cast)
})

const durationtext = computed(() => {
  const dur = data.value?.duration ?? ''
  return dur ? expandDuration(dur) : 'Instantaneous'
})

</script>

<template>
  <div class="spell-table">
    <span v-if="data === null">No data for {{ spell.title }}.</span>
    <table v-else class="nostripes">
      <tbody>
        <tr>
          <SpellTableCell title="Level">{{ spellLevel(data.level) }}</SpellTableCell>
          <SpellTableCell title="Casting Time"
            >{{ castText }}&nbsp;
            <Icon v-if="spell.reactionTo" name="fa6-solid:asterisk" class="marker" />
            <Icon v-else-if="data.ritual" name="fa6-solid:registered" title="Ritual" class="marker" />
          </SpellTableCell>
          <SpellTableCell title="Range/Area">
            {{ rangeText }}
            <span v-for="(feet, area) in data.area" :key="area">
              &nbsp;({{ feet }} ft <Icon :name="spellAreaIcon(area)" />)
            </span>
          </SpellTableCell>
          <SpellTableCell title="Components">
            {{ componentsText }}
            <Icon v-if="spell.materialComponent" name="fa6-solid:asterisk" />
          </SpellTableCell>
        </tr>
        <tr>
          <SpellTableCell title="Duration">
            <Icon v-if="data.concentration" name="fa6-solid:copyright" title="Concentration" class="marker" />
            {{ durationtext }}
          </SpellTableCell>
          <SpellTableCell title="School">{{ titleCase(data.school) }}</SpellTableCell>
          <SpellTableCell title="Attack/Save">
            {{ saves.length ? saves.slice(0, 2).join(', ') : 'None' }}
            {{ saves.length > 2 ? '...' : '' }}
          </SpellTableCell>
          <SpellTableCell title="Damage/Effect">
            {{ effects.length ?
              effects
                .slice(0, 2)
                .map((eff) => titleCase(eff))
                .join(', ')
              : 'None'
            }}
            {{ effects.length > 2 ? '...' : '' }}
          </SpellTableCell>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped lang="postcss">
.spell-table {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;

  border-bottom: 2px solid var(--primary);
  margin-bottom: 0.5em;

  > table {
    width: 100%;
    margin-bottom: 0.5em;

    .icon {
      color: var(--text-highlight);
    }

    .marker {
      margin-right: 0.5em;
    }

    td {
      width: 25%;
      max-width: 25%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
